import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { useUtils } from './useUtils';
import { useOpenState } from './useOpenState';
import { getDisplayDate, validate } from '../../_helpers/text-field-helper';
import { useCallback, useDebugValue, useEffect, useMemo, useState, useRef } from 'react';
var useValueToDate = function useValueToDate(utils, _ref) {
  var value = _ref.value,
    initialFocusedDate = _ref.initialFocusedDate;
  var nowRef = useRef(utils.date());
  var date = utils.date(value || initialFocusedDate || nowRef.current);
  return date && utils.isValid(date) ? date : nowRef.current;
};
function useDateValues(props, options) {
  var utils = useUtils();
  var date = useValueToDate(utils, props);
  var format = props.format || options.getDefaultFormat();
  return {
    date: date,
    format: format
  };
}
export function usePickerState(props, options) {
  var autoOk = props.autoOk,
    disabled = props.disabled,
    onAccept = props.onAccept,
    _onChange = props.onChange,
    onError = props.onError,
    value = props.value,
    variant = props.variant;
  var utils = useUtils();
  var _useOpenState = useOpenState(props),
    isOpen = _useOpenState.isOpen,
    setIsOpen = _useOpenState.setIsOpen;
  var _useDateValues = useDateValues(props, options),
    date = _useDateValues.date,
    format = _useDateValues.format;
  var _useState = useState(date),
    _useState2 = _slicedToArray(_useState, 2),
    pickerDate = _useState2[0],
    setPickerDate = _useState2[1];
  useEffect(function () {
    // if value was changed in closed state - treat it as accepted
    if (!isOpen && !utils.isEqual(pickerDate, date)) {
      setPickerDate(date);
    }
  }, [date, isOpen, pickerDate, utils]);
  var acceptDate = useCallback(function (acceptedDate) {
    setIsOpen(false);
    _onChange(acceptedDate);
    if (onAccept) {
      onAccept(acceptedDate);
    }
  }, [onAccept, _onChange, setIsOpen]);
  var wrapperProps = useMemo(function () {
    return {
      format: format,
      open: isOpen,
      onClear: function onClear() {
        return acceptDate(null);
      },
      onAccept: function onAccept() {
        return acceptDate(pickerDate);
      },
      onSetToday: function onSetToday() {
        return setPickerDate(utils.date());
      },
      onDismiss: function onDismiss() {
        setIsOpen(false);
      }
    };
  }, [acceptDate, format, isOpen, pickerDate, setIsOpen, utils]);
  var pickerProps = useMemo(function () {
    return {
      date: pickerDate,
      onChange: function onChange(newDate) {
        var isFinish = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
        setPickerDate(newDate);
        if (isFinish && autoOk) {
          acceptDate(newDate);
          return;
        } // simulate autoOk, but do not close the modal

        if (variant === 'inline' || variant === 'static') {
          _onChange(newDate);
          onAccept && onAccept(newDate);
        }
      }
    };
  }, [acceptDate, autoOk, onAccept, _onChange, pickerDate, variant]);
  var validationError = validate(value, utils, props);
  useEffect(function () {
    if (validationError && onError) {
      onError(validationError, value);
    }
  }, [onError, validationError, value]);
  var inputValue = getDisplayDate(date, format, utils, value === null, props);
  var inputProps = useMemo(function () {
    return {
      inputValue: inputValue,
      validationError: validationError,
      onClick: function onClick() {
        return !disabled && setIsOpen(true);
      }
    };
  }, [disabled, inputValue, setIsOpen, validationError]);
  var pickerState = {
    pickerProps: pickerProps,
    inputProps: inputProps,
    wrapperProps: wrapperProps
  };
  useDebugValue(pickerState);
  return pickerState;
}